import React, { useState, useEffect } from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {Flex, Image, Box, Text, HStack, Badge, Breadcrumb, BreadcrumbItem, BreadcrumbLink} from '@chakra-ui/react';
import {MdAdd} from 'react-icons/md';
import { ChevronRightIcon } from '@chakra-ui/icons';
import Button from './shared/Button';
import ThemeToggler from './ThemeToggler';
import Disclaimer from './Disclaimer';
import {Container} from '@chakra-ui/react';
import sessionManager from '../utils/SessionManager';
import personaServices from '../services/PersonaServices';
import { Persona, SessionMetadata } from '../types';

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activePersona, setActivePersona] = useState<Persona | null>(null);
    const [activeSession, setActiveSession] = useState<SessionMetadata | null>(null);

    const handleCreatePersona = () => {
        navigate('/create-persona');
    };

    const handleLogoClick = () => {
        navigate('/');
    };

    useEffect(() => {
        const loadActiveData = async () => {
            const pathParts = location.pathname.split('/').filter(Boolean);
            
            // Reset state
            setActivePersona(null);
            setActiveSession(null);

            // Check if we're on a persona or chat route
            if (pathParts[0] === 'persona' || pathParts[0] === 'chat') {
                const personaId = pathParts[1];
                
                if (personaId) {
                    try {
                        const persona = await personaServices.getPersona(personaId, 'web1');
                        setActivePersona(persona);

                        // If we're on a chat route, also get session info
                        if (pathParts[0] === 'chat' && pathParts[2]) {
                            const sessionId = pathParts[2];
                            const session = sessionManager.getSession(personaId, sessionId);
                            if (session) {
                                setActiveSession(session.metadata);
                            }
                        }
                    } catch (error) {
                        console.error('Error loading navbar data:', error);
                    }
                }
            }
        };

        loadActiveData();
    }, [location.pathname]);

    const renderNavContent = () => {
        if (location.pathname === '/') {
            return (
                <Text fontSize="lg" fontWeight="bold" cursor="pointer" onClick={handleLogoClick}>
                    AI Personas
                </Text>
            );
        }

        if (activePersona) {
            return (
                <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />}>
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => navigate('/')}>
                            Home
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => navigate(`/persona/${activePersona.id}`)}>
                            {activePersona.name || 'Unnamed Persona'}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    {activeSession && (
                        <BreadcrumbItem isCurrentPage>
                            <BreadcrumbLink>{activeSession.title}</BreadcrumbLink>
                        </BreadcrumbItem>
                    )}
                </Breadcrumb>
            );
        }

        return (
            <Text fontSize="lg" fontWeight="bold" cursor="pointer" onClick={handleLogoClick}>
                AI Personas
            </Text>
        );
    };

    return (
        <Container maxW="container.xl" p={0}>
            <Flex align="center" justify="space-between" px={4} py={3} boxShadow="sm">
                <HStack spacing={4}>
                    <Image 
                        src="/logo.png" 
                        alt="AiPersonas" 
                        boxSize="40px" 
                        cursor="pointer"
                        onClick={handleLogoClick}
                    />
                    <Box>
                        {renderNavContent()}
                        {activePersona && (
                            <HStack spacing={2} mt={1}>
                                <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                    {activePersona.provider}
                                </Badge>
                                {activeSession && (
                                    <Badge colorScheme="green" variant="subtle" fontSize="xs">
                                        {activeSession.message_count} messages
                                    </Badge>
                                )}
                            </HStack>
                        )}
                    </Box>
                </HStack>

                <Flex align="center">
                    <Button
                        leftIcon={<MdAdd/>}
                        onClick={handleCreatePersona}
                        mr={2}
                        bg="orange.200"
                        color="orange.800"
                        _hover={{bg: "orange.300"}}
                        height="32px"
                        fontSize="sm"
                        px={3}
                        display="none"
                    >
                        Create Persona
                    </Button>
                    <Disclaimer/>
                    <ThemeToggler display={{base: 'none', md: 'flex'}} align="center" ml={2}/>
                </Flex>
            </Flex>
        </Container>
    );
};

export default Navbar;